import axios from 'axios';

import { OrganizationSearchResult } from '@/models/organizationSearchResult';
import { AttributeItem } from '@/models/attributeItem';
import { CountyItem } from '@/models/CountyItem';
import { ORGANIZATION_TYPES, ORGANIZATION_TYPES_SHORT } from '@/utils/constants';
import logger from '@/utils/logger';

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL + '/registration/';

const orgTypeMapping = Object.keys(ORGANIZATION_TYPES_SHORT).reduce((mapping, key) => {
  mapping[ORGANIZATION_TYPES_SHORT[key]] = ORGANIZATION_TYPES[key];
  return mapping;
}, {});

const getOrgTypeFromShort = (orgTypeShortDesc) => orgTypeMapping[orgTypeShortDesc] || '';

/**
 * Registers an organization.
 *
 * @param {Object} organization - The organization object to register.
 * @returns {Promise<Boolean>} True, if the registration is successful otherwise, false.
 * @throws Will throw an error if the `organization` parameter is missing or if the registration fails.
 */
export const registerOrganization = async (organization) => {
  if (!organization) {
    throw new Error('Missing required parameters');
  }

  try {
    const response = await axios.post(`${baseUrl}registerorganization`, organization, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    logger.error('Error registering organization', error);
    throw error;
  }
};

/**
 * Registers a contact.
 *
 * @param {Object} contact - The contact object to register.
 * @returns {Promise<Object>} The response data if the registration is successful.
 * @throws Will throw an error if the `contact` parameter is missing or if the registration fails.
 */
export const registerContact = async (contact) => {
    if (!contact) {
      throw new Error('Missing required parameters');
    }
  
    try {
      await axios.post(`${baseUrl}registercontact`, contact, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      logger.error('Error registering contact', error);
      throw error;
    }
  };

/**
 * Approves a contact by its ID.
 *
 * @param {string} contactId - The ID of the contact to approve.
 * @returns {Promise<Object>} The response data if the approval is successful.
 * @throws Will throw an error if the `contactId` parameter is missing or if the approval fails.
 */
export const approveContact = async (contactId) => {
    if (!contactId) {
        throw new Error('Missing required parameters');
    }
  
    try {
        const response = await axios.post(`${baseUrl}approvecontact?id=${encodeURIComponent(contactId)}`);
        return response.data;
    } catch (error) {
        logger.error('Error approving contact', error);
        throw error;
    }
};

/**
 * Searches for organizations based on the provided search query.
 *
 * @param {string} search - The search query to filter organizations.
 * @param {string} orgTypeShortDesc - The short description of the organization type.
 * @returns {Promise<Object>} The response data containing the search results.
 * @throws Will throw an error if the `search` parameter is missing or if the search fails.
 */
export const searchOrganization = async (search, orgTypeShortDesc) => {
    if (!search || !orgTypeShortDesc) {
      throw new Error('Missing required parameters');
    }

    if (!Object.values(ORGANIZATION_TYPES_SHORT).includes(orgTypeShortDesc)) {
      throw new Error(`Invalid organization type: ${orgTypeShortDesc}`);
    }
  
    const orgType = getOrgTypeFromShort(orgTypeShortDesc);

    try {
      const url = `${baseUrl}search?search=${encodeURIComponent(search)}&type=${encodeURIComponent(orgType)}`;
      const response = await axios.get(url);
      if (Array.isArray(response.data)) {
        const organizations = response.data.map(item => new OrganizationSearchResult(item));
        return organizations;
      } else {
        throw new Error('Invalid response data format');
      }
    } catch (error) {
      logger.error('Error searching organization', error);
      throw error;
    }
  };

/**
 * Retrieves an organization by its ID.
 *
 * @param {string} id - The ID of the organization to retrieve.
 * @returns {Promise<Object>} The organization data if found.
 * @throws Will throw an error if the `id` parameter is missing or if the retrieval fails.
 */
export const getOrganization = async (id) => {
    if (!id) {
      throw new Error('Missing required parameters');
    }
  
    try {
      const url = `${baseUrl}getorganization?id=${encodeURIComponent(id)}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        throw new Error('Organization not found');
      } else {
        logger.error('Error getting organization', error);
        throw error;
      }
    }
  };

/**
 * Checks if a contact email exists.
 *
 * @param {string} email - The email address to check.
 * @param {boolean} isHceOrganization - Indicates if the organization is an HCE organization.
 * @returns {Promise<boolean>} True if the email exists, otherwise false.
 * @throws Will throw an error if the `email` parameter is missing or if the check fails.
 */
export const contactEmailExists = async (email, isHceOrganization) => {
    if (!email) {
      throw new Error('Missing required parameters');
    }


    try {
      const url = `${baseUrl}contactemailexists?email=${encodeURIComponent(email)}&isHceOrganization=${isHceOrganization}`;
      const response = await axios.get(url);
      return response.data == true;
    } catch (error) {
      logger.error('Error verifying email address', error);
      throw error;
    }
  };

/**
 * Retrieves all organization types.
 *
 * @returns {Promise<AttributeItem[]>} An array of organization types.
 * @throws Will throw an error if the retrieval fails.
 */
export const getOrganizationTypes = async () => {
    try {
      const response = await axios.get(`${baseUrl}getorganizationtypes`);
      if (Array.isArray(response.data)) {
        const orgTypes = response.data.map(item => new AttributeItem(item));
        return orgTypes;
      } else {
        throw new Error('Invalid response data format');
      }
    } catch (error) {
      logger.error('Error getting organization types', error);
    }
  };

/**
 * Retrieves all counties.
 *
 * @returns {Promise<CountyItem[]>} An array of counties.
 * @throws Will throw an error if the retrieval fails.
 */
export const getCounties = async () => {
    try {
      const response = await axios.get(`${baseUrl}getcounties`);
      if (Array.isArray(response.data)) {
        const counties = response.data.map(item => new CountyItem(item));
        return counties;
      } else {
        throw new Error('Invalid response data format');
      }
    } catch (error) {
      logger.error('Error getting counties', error);
    }
  };


